import React from "react"
import styled from "styled-components"
import devices from "styles/devices"
import { formatFullAppointmentDateTime } from "lib/FormatDate"
import { MortgageExpertCardComponent } from "./MortgageExpertCard"
import { StatusData } from "../utils/statusApiUtils"

interface MortgageExpert {
  name: string
  title: string
  email: string
  avatar: string
}

interface CallbackConfirmationCardProps {
  mortgageExpert: MortgageExpert
  appointmentDate?: Date
  fetchedStatus?: StatusData | null
}

const CallbackCard = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 24px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
`

const CallbackHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const CallbackTitle = styled.h3`
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

const CallbackDescription = styled.p`
  font-weight: 500;
  font-size: 16px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

const AppointmentDetails = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 16px;
  gap: 16px;
  @media screen and ${devices.bp_sm} {
    flex-direction: row;
  }
`

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  margin: 16px 0;
`

const DateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 82px;
  height: 120px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 8px;
  flex-shrink: 0;
  box-shadow: 0 4px 4px 0px rgba(0, 9, 40, 0.08);
  @media screen and ${devices.bp_sm} {
    height: unset;
  }
`

const DateMonth = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 26px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-transform: uppercase;
  font-variant-numeric: lining-nums tabular-nums;
`

const DateDay = styled.span`
  font-weight: 600;
  font-size: 26px;
  line-height: 36px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: lining-nums proportional-nums;
`

const AppointmentDetailsDateContainer = styled.div`
  display: flex;
`

const AppointmentTimeMobile = styled.p`
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.text.primary};
  justify-self: center;
  align-content: center;
  margin: 16px;

  @media screen and ${devices.bp_sm} {
    display: none;
    margin: 0;
  }
`

const AppointmentTime = styled(AppointmentTimeMobile)`
  display: none;

  @media screen and ${devices.bp_sm} {
    display: inline;
  }
`

const AppointmentDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media screen and ${devices.bp_sm} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  @media screen and ${devices.bp_sm} {
    flex-direction: column;
  }
`

const DetailLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`

const DetailValue = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex: 1;
  justify-content: flex-end;
  @media screen and ${devices.bp_sm} {
    display: inline;
  }
`

export const CallbackConfirmationCard: React.FC<CallbackConfirmationCardProps> = ({
  mortgageExpert,
  appointmentDate,
  fetchedStatus,
}) => {
  const formatPropertyAddress = () => {
    return fetchedStatus?.propertyAddress || "N/A"
  }

  const formatOfferAmount = () => {
    // Handle null, undefined, and zero cases properly
    if (!fetchedStatus?.offerAmount && fetchedStatus?.offerAmount !== 0) {
      return "N/A"
    }
    // Format numeric value as currency
    return `£${fetchedStatus.offerAmount.toLocaleString()}`
  }

  const getDateElements = () => {
    if (!appointmentDate) return { month: "", day: "" }
    const month = appointmentDate.toLocaleDateString("en-GB", { month: "short" })
    const day = appointmentDate.getDate().toString()
    return { month, day }
  }

  const getFormattedDateTime = () => {
    if (!appointmentDate) return "Appointment details pending"
    return formatFullAppointmentDateTime(appointmentDate)
  }

  const { month, day } = getDateElements()

  return (
    <CallbackCard>
      <CallbackHeader>
        <CallbackTitle>Callback confirmed</CallbackTitle>
        <CallbackDescription>
          We've passed your availability on to {mortgageExpert.name}. In the meantime, they will
          review your information to prepare for your call and will be in touch shortly.
        </CallbackDescription>
      </CallbackHeader>

      <MortgageExpertCardComponent mortgageExpert={mortgageExpert} />

      <AppointmentDetails>
        {appointmentDate && (
          <AppointmentDetailsDateContainer>
            <DateBox>
              <DateDay>{day}</DateDay>
              <DateMonth>{month}</DateMonth>
            </DateBox>
            <AppointmentTimeMobile>{getFormattedDateTime()}</AppointmentTimeMobile>
          </AppointmentDetailsDateContainer>
        )}

        <DetailsColumn>
          <AppointmentTime>{getFormattedDateTime()}</AppointmentTime>
          <AppointmentDetailsGrid>
            {fetchedStatus?.propertyAddress && (
              <DetailItem>
                <DetailLabel>Property</DetailLabel>
                <DetailValue>{formatPropertyAddress()}</DetailValue>
              </DetailItem>
            )}
            {fetchedStatus?.offerAmount && (
              <DetailItem>
                <DetailLabel>Offer amount</DetailLabel>
                <DetailValue>{formatOfferAmount()}</DetailValue>
              </DetailItem>
            )}
          </AppointmentDetailsGrid>
        </DetailsColumn>
      </AppointmentDetails>
    </CallbackCard>
  )
}
